import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IeltsWritingUpgradeTable } from "@/components";
import { aiUpgradeManager, useAiUpgradeState } from "@/services/aiUpgradeManager";

export const GlobalAiUpgradeWidget: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const location = useLocation();

  const {
    isGenerating,
    progress,
    targetBand,
    currentBand,
    prompt,
    originalEssay,
    upgradedResult,
    error,
    modalOpen,
    notifyCompleted,
    pageUrl,
    onApplyCallback,
  } = useAiUpgradeState();

  const [showBandSelect, setShowBandSelect] = useState(false);
  const [localTargetBand, setLocalTargetBand] = useState<number>(0);

  // Compute available target bands from currentBand
  const bandOptions = useMemo(() => {
    if (!currentBand) return [];
    const options: number[] = [];
    let start = Math.floor(currentBand * 2 + 1) / 2;
    if (start <= currentBand) start += 0.5;
    for (let b = Math.max(start, 5.0); b <= 9.0; b += 0.5) {
      options.push(b);
    }
    return options;
  }, [currentBand]);

  if (!isGenerating && !upgradedResult && !modalOpen && !notifyCompleted) {
    return null;
  }

  const handleOpenDetails = () => {
    if (pageUrl && location.pathname !== pageUrl) {
      navigate(pageUrl);
    }
    aiUpgradeManager.openModal();
  };

  const handleApply = () => {
    if (!upgradedResult?.upgradedEssay) return;
    const cleanText = upgradedResult.upgradedEssay.replace(/<\/?[^>]+(>|$)/g, "").trim();
    if (onApplyCallback) {
      onApplyCallback(cleanText);
    }
    aiUpgradeManager.closeModal();
  };

  return (
    <>
      {/* 1. Mini Floating Progress Widget (Appears globally across all pages when generating & modal is hidden) */}
      {isGenerating && !modalOpen && (
        <Paper
          elevation={8}
          onClick={handleOpenDetails}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 99999,
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderRadius: "16px",
            cursor: "pointer",
            background: isDark
              ? "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)"
              : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(139, 92, 246, 0.3)",
            boxShadow: "0 10px 30px rgba(139, 92, 246, 0.25)",
            transition: "all 0.25s ease-in-out",
            "&:hover": {
              transform: "translateY(-4px) scale(1.02)",
              boxShadow: "0 14px 36px rgba(139, 92, 246, 0.35)",
              borderColor: "#8B5CF6",
            },
          }}
        >
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              variant="determinate"
              value={progress}
              size={44}
              thickness={4.5}
              sx={{
                color: "#8B5CF6",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AutoAwesomeIcon sx={{ color: "#EC4899", fontSize: 20 }} />
            </Box>
          </Box>

          <Box sx={{ pr: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isDark ? "#fff" : "#1E293B" }}>
                Đang nâng Band {targetBand || ""}
              </Typography>
              <Chip
                label={`${progress}%`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  fontWeight: 800,
                  background: "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
                  color: "#fff",
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", fontSize: "0.75rem", mt: 0.2 }}
            >
              Bấm vào đây để xem chi tiết
            </Typography>
          </Box>
        </Paper>
      )}

      {/* 2. Global Band Upgrade Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => {
          setShowBandSelect(false);
          aiUpgradeManager.closeModal();
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            bgcolor: isDark ? "#1E293B" : "#fff",
            backgroundImage: "none",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)"}`,
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <PsychologyIcon sx={{ color: "#8B5CF6", fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: isDark ? "#fff" : "#1E293B" }}>
                Gợi ý nâng band điểm viết
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {showBandSelect
                  ? `Chọn band điểm mục tiêu mới (hiện tại: Band ${currentBand})`
                  : `Nâng cấp từ Band ${currentBand} lên Band ${targetBand} bằng mô hình phân tích chuyên sâu của hệ thống.`}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => aiUpgradeManager.closeModal()}
            sx={{
              color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)",
              "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3, borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" }}>
          {/* Band selection view - shown after clicking "Chọn band điểm khác" */}
          {showBandSelect && (
            <Box sx={{ py: 5, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <Typography variant="body1" sx={{ textAlign: "center", maxWidth: 500, color: "text.secondary" }}>
                Hệ thống sẽ giữ nguyên ý tưởng bài làm của bạn, cải thiện từ vựng, ngữ pháp và độ mạch lạc để nâng lên
                band điểm mong muốn.
              </Typography>
              <FormControl sx={{ minWidth: 240 }}>
                <InputLabel id="global-target-band-label">Chọn band điểm mục tiêu</InputLabel>
                <Select
                  labelId="global-target-band-label"
                  value={localTargetBand}
                  label="Chọn band điểm mục tiêu"
                  onChange={(e) => setLocalTargetBand(Number(e.target.value))}
                  sx={{ borderRadius: "12px" }}
                >
                  {bandOptions.map((b) => (
                    <MenuItem key={b} value={b}>
                      IELTS Band {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disabled={!localTargetBand}
                onClick={() => {
                  setShowBandSelect(false);
                  aiUpgradeManager.startUpgrade({
                    prompt,
                    originalEssay,
                    currentBand,
                    targetBand: localTargetBand,
                    pageUrl,
                    onApplyCallback,
                  });
                }}
                startIcon={<AutoAwesomeIcon />}
                sx={{
                  borderRadius: "12px",
                  fontWeight: 700,
                  textTransform: "none",
                  px: 4,
                  py: 1.5,
                  background: "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
                  boxShadow: "0 8px 24px rgba(139, 92, 246, 0.3)",
                }}
              >
                Bắt đầu nâng lên Band {localTargetBand}
              </Button>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                * Chi phí nâng band được khấu trừ vào hạn mức học tập của bạn.
              </Typography>
            </Box>
          )}

          {/* Loading state when generating but no result yet */}
          {!showBandSelect && isGenerating && !upgradedResult && (
            <Box
              sx={{
                py: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                  variant="determinate"
                  value={progress}
                  size={72}
                  thickness={4}
                  sx={{
                    color: "#8B5CF6",
                    "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                  }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: "absolute",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AutoAwesomeIcon sx={{ color: "#EC4899", fontSize: 28 }} />
                </Box>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: isDark ? "#fff" : "#1E293B", mb: 0.5 }}>
                  Hệ thống đang viết gợi ý nâng Band {targetBand}...
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                  {progress}% hoàn thành — Vui lòng đợi hoặc ẩn cửa sổ này để tiếp tục dùng app.
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  💡 Hệ thống vẫn xử lý ngầm kể cả khi bạn chuyển sang trang khác.
                </Typography>
              </Box>
            </Box>
          )}

          {upgradedResult && (
            <Box
              sx={{
                ...(isGenerating && {
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backdropFilter: "blur(8px)",
                }),
                mb: 2.5,
              }}
            >
              {isGenerating ? (
                <Alert
                  severity="info"
                  icon={<CircularProgress size={20} sx={{ color: "#8B5CF6" }} />}
                  sx={{
                    borderRadius: "14px",
                    background: isDark ? "rgba(139, 92, 246, 0.15)" : "rgba(139, 92, 246, 0.08)",
                    border: "1px solid rgba(139, 92, 246, 0.25)",
                    boxShadow: "0 4px 16px rgba(139, 92, 246, 0.15)",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#E9D5FF" : "#6B21A8" }}>
                    Hệ thống đang tiếp tục tối ưu các đoạn văn tiếp theo... ({progress}%)
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: isDark ? "rgba(255,255,255,0.7)" : "text.secondary" }}
                  >
                    💡 Bạn có thể đọc trước các đoạn văn đã hoàn thành bên dưới. Hệ thống vẫn sẽ xử lý ngầm kể cả khi
                    chuyển trang.
                  </Typography>
                </Alert>
              ) : (
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon sx={{ color: "#10B981" }} />}
                  sx={{
                    borderRadius: "14px",
                    background: isDark ? "rgba(16, 185, 129, 0.15)" : "rgba(16, 185, 129, 0.08)",
                    border: "1px solid rgba(16, 185, 129, 0.25)",
                    boxShadow: "0 4px 16px rgba(16, 185, 129, 0.15)",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#A7F3D0" : "#065F46" }}>
                    Đã tải xong toàn bộ gợi ý nâng band điểm!
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {!showBandSelect && upgradedResult && (
            <IeltsWritingUpgradeTable
              prompt={prompt}
              originalEssay={originalEssay}
              upgradedResult={upgradedResult}
              currentBand={currentBand}
              targetBand={targetBand}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{ p: 3, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)" }}
        >
          <Button
            variant="outlined"
            onClick={() => aiUpgradeManager.closeModal()}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
          >
            {isGenerating ? "Ẩn cửa sổ (tiếp tục chạy ngầm)" : "Đóng"}
          </Button>

          {!showBandSelect && upgradedResult && !isGenerating && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                aiUpgradeManager.resetResult();
                setLocalTargetBand(bandOptions[0] ?? 0);
                setShowBandSelect(true);
              }}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                color: "#8B5CF6",
                borderColor: "rgba(139, 92, 246, 0.4)",
                "&:hover": { borderColor: "#8B5CF6", bgcolor: "rgba(139, 92, 246, 0.08)" },
              }}
            >
              Chọn band điểm khác
            </Button>
          )}

          {upgradedResult && (
            <Button
              variant="contained"
              onClick={handleApply}
              startIcon={<CheckCircleIcon />}
              sx={{
                borderRadius: "10px",
                fontWeight: 700,
                textTransform: "none",
                background: "linear-gradient(135deg, #0068FF 0%, #0056D2 100%)",
              }}
            >
              Đồng ý thay đổi câu trả lời
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 3. Global Notification Toast when background generation finishes */}
      <Snackbar
        open={notifyCompleted}
        autoHideDuration={12000}
        onClose={() => aiUpgradeManager.setState({ notifyCompleted: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ zIndex: 999999 }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => aiUpgradeManager.setState({ notifyCompleted: false })}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                aiUpgradeManager.setState({ notifyCompleted: false });
                handleOpenDetails();
              }}
              sx={{
                fontWeight: 800,
                textTransform: "none",
                bgcolor: "rgba(255, 255, 255, 0.25)",
                color: "#fff",
                px: 1.5,
                py: 0.5,
                borderRadius: "8px",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.4)" },
              }}
            >
              Xem kết quả
            </Button>
          }
          sx={{
            borderRadius: "14px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            fontWeight: 600,
            alignItems: "center",
            py: 1,
            px: 2,
          }}
        >
          🎉 Đã tạo xong bài viết gợi ý nâng Band {targetBand}!
        </Alert>
      </Snackbar>
    </>
  );
};

export default GlobalAiUpgradeWidget;
